import { useId, useState } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'

interface AccordionItem {
    question: string
    answer: string
}

type AccordionVariant = 'lines' | 'pills'

interface AccordionProps {
    items: Array<AccordionItem>
    variant?: AccordionVariant
    defaultOpen?: number | null
    className?: string
}

/**
 * Single-open accordion with a smooth height transition.
 * `lines` separates questions with hairlines, `pills` puts each one in its own rounded row.
 */
export const Accordion = ({ items, variant = 'lines', defaultOpen = 0, className }: AccordionProps) => {
    const [open, setOpen] = useState<number | null>(defaultOpen)
    const baseId = useId()
    const pills = variant === 'pills'

    return (
        <div className={cn(pills ? 'space-y-3' : 'divide-y divide-white/[0.08] border-y border-white/[0.08]', className)}>
            {items.map((item, index) => {
                const expanded = open === index
                const buttonId = `${baseId}-button-${index}`
                const panelId = `${baseId}-panel-${index}`

                return (
                    <div
                        key={item.question}
                        className={cn(
                            pills && 'rounded-[26px] bg-white/[0.035] px-5 ring-1 ring-inset transition-[background-color,box-shadow] duration-500 ease-cinema sm:px-6',
                            pills && (expanded ? 'bg-white/[0.06] ring-citron/25' : 'ring-white/[0.07] hover:bg-white/[0.05]')
                        )}
                    >
                        <h3>
                            <button
                                id={buttonId}
                                type='button'
                                aria-expanded={expanded}
                                aria-controls={panelId}
                                onClick={() => setOpen(expanded ? null : index)}
                                className={cn('group flex w-full items-center justify-between gap-6 text-left', pills ? 'py-4' : 'py-6')}
                            >
                                <span
                                    className={cn(
                                        'tracking-[-0.02em] text-cream transition-colors',
                                        pills ? 'text-[15px] sm:text-base' : 'text-lg group-hover:text-citron sm:text-xl'
                                    )}
                                >
                                    {item.question}
                                </span>

                                <span
                                    aria-hidden='true'
                                    className={cn(
                                        'grid shrink-0 place-items-center transition-all duration-500 ease-cinema',
                                        pills ? 'size-9 rounded-[12px]' : 'size-9 rounded-full ring-1 ring-white/15',
                                        pills && !expanded && 'bg-white/[0.06] text-cream/70 group-hover:text-cream',
                                        pills && expanded && 'rotate-45 bg-[linear-gradient(135deg,#e2f37a,#8fae62)] text-black shadow-[0_0_28px_-2px_rgba(226,243,122,0.55)]',
                                        !pills && (expanded ? 'rotate-45 bg-cream text-black' : 'text-cream')
                                    )}
                                >
                                    <Plus className='size-4' />
                                </span>
                            </button>
                        </h3>

                        <div
                            id={panelId}
                            role='region'
                            aria-labelledby={buttonId}
                            className={cn('grid transition-[grid-template-rows] duration-500 ease-cinema', expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}
                        >
                            <div className='overflow-hidden'>
                                <p className={cn('max-w-2xl leading-relaxed text-cream/65', pills ? 'pb-5 pr-12 text-sm sm:text-[15px]' : 'pb-6 text-[15px]')}>
                                    {item.answer}
                                </p>
                            </div>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
